import React, {
  type ComponentProps,
  type ReactElement,
  type MouseEventHandler,
  type PropsWithChildren,
  type CSSProperties,
} from 'react';

import * as Ariakit from '@ariakit/react';
import cn from 'classnames';

import { SizeContextProvider, useSizeContext } from '../Hooks/useSizeContext';
import * as styles from './styles.module.css';

export function Trigger(props: ComponentProps<typeof Ariakit.MenuButton>) {
  return <Ariakit.MenuButton {...props} />;
}

export function Provider(props: ComponentProps<typeof Ariakit.MenuProvider>) {
  return <Ariakit.MenuProvider {...props} />;
}

export function Item({
  onClick,
  addition,
  children,
  className = '',
  ...rest
}: PropsWithChildren<{
  onClick?: MouseEventHandler<HTMLDivElement>;
  addition?: ReactElement | string | boolean;
  className?: string;
}> &
  ComponentProps<typeof Ariakit.MenuItem>) {
  return (
    <Ariakit.MenuItem
      className={cn([styles.dropdownMenuItem, className])}
      onClick={onClick}
      focusOnHover={false}
      {...rest}
    >
      <span className={styles.text}>{children}</span>
      {addition ? <div>{addition}</div> : null}
    </Ariakit.MenuItem>
  );
}

export function ActionList({
  children,
  portal = false,
  style,
  className = '',
  ...rest
}: PropsWithChildren<{
  portal?: boolean;
  style?: CSSProperties;
  className?: string;
}> &
  ComponentProps<typeof Ariakit.Menu>) {
  return (
    <Ariakit.Menu
      gutter={16}
      portal={portal}
      className={cn([styles.dropdownMenuContent, className])}
      style={style}
      fixed
      {...rest}
    >
      {children}
    </Ariakit.Menu>
  );
}

export function ExternalLink({
  href,
  onClick,
  children,
  prepend,
  ...rest
}: PropsWithChildren<{
  href: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  prepend?: ReactElement;
}> &
  ComponentProps<typeof Ariakit.MenuItem>) {
  return (
    <Ariakit.MenuItem
      className={styles.dropdownMenuItem}
      onClick={onClick}
      focusOnHover={false}
      render={
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <a href={href} target="_blank" rel="noreferrer" />
      }
      {...rest}
    >
      {prepend && <span className={styles.icon}>{prepend}</span>}
      <span className={styles.text}>{children}</span>
    </Ariakit.MenuItem>
  );
}

type RadioItemSize = 'large' | 'medium';

type RadioItemProps = {
  size?: RadioItemSize;
  variant?: 'ordinary' | 'radio';
};

const sizeInPx: Record<RadioItemSize, string> = {
  large: '24px',
  medium: '20px',
};

export function RadioItem({
  size = 'medium',
  variant = 'radio',
  ...props
}: RadioItemProps & Omit<ComponentProps<typeof Ariakit.MenuItemRadio>, 'variant'>) {
  return (
    <SizeContextProvider value={size}>
      <Ariakit.MenuItemRadio
        {...props}
        className={cn([styles.radioItem, styles[size], styles[variant]])}
      />
    </SizeContextProvider>
  );
}

export function RadioItemCheck({ ...rest }) {
  const size = useSizeContext() as RadioItemSize;

  if (!size) {
    throw new Error('RadioItemCheck must be wrapped in SizeContextProvider');
  }

  return (
    <Ariakit.MenuItemCheck
      {...rest}
      render={
        <div className={styles.menuItemCheckWrapper}>
          <div className={cn([styles.menuItemCheck, styles[size]])} />
        </div>
      }
      style={{ width: sizeInPx[size], height: sizeInPx[size] }}
    />
  );
}

export function Divider({ variant = 'line' }: { variant?: 'line' | 'empty' }) {
  return (
    <Ariakit.MenuSeparator render={<div className={cn([styles.divider, styles[variant]])} />} />
  );
}

export function TextDivider({ children }: { children: React.ReactNode }) {
  return <Ariakit.MenuSeparator render={<div className={styles.textDivider}>{children}</div>} />;
}
